/**
 * Прогресс между забежками: уровни героев (Level), звёзды (Stars), Tech — цены, эффекты, «сила» для HUD.
 *
 * Уровень героя начинается с {@link HERO_START_LEVEL} = 1, уровень Tech — с 0 (нет записи — 0).
 * Цена — по текущему уровню: `levelCost(level)` — покупка `level → level + 1` (как в `CostCurve` contracts).
 */
import {
  PERMILLE,
  TICK_RATE,
  type BalanceData,
  type HeroDef,
  type HeroId,
  type HeroProgress,
  type StatModifier,
  type TechDef,
} from "@newfungame/contracts";
import { curveCost } from "./curve";
import { foldLevels } from "./modifiers";

/** Уровень нового героя. */
export const HERO_START_LEVEL = 1;
/** Звёзд у героя максимум. */
export const MAX_STARS = 5;

/** Описание героя; бросает, если такого нет в данных. */
export function heroDef(b: BalanceData, heroId: HeroId): HeroDef {
  const h = b.heroes.find((x) => x.id === heroId);
  if (!h) throw new RangeError(`unknown hero ${heroId}`);
  return h;
}

/** Описание Tech или `undefined`. */
export function techDef(b: BalanceData, techId: string): TechDef | undefined {
  return b.tech.find((t) => t.id === techId);
}

/* ─────────────── Level ─────────────── */

/** Цена уровня `level → level + 1`; `null` — герой на максимальном уровне. */
export function heroLevelCost(b: BalanceData, heroId: HeroId, level: number): number | null {
  const h = heroDef(b, heroId);
  if (level >= h.maxLevel) return null;
  return curveCost(h.levelCost, level);
}

/** Множитель урона от уровня, промилле: `1000 + damagePerLevel · (level − 1)` (+8 % от базового за уровень). */
export function heroLevelDamagePermille(hero: HeroDef, level: number): number {
  return PERMILLE + hero.damagePerLevelPermille * (Math.max(HERO_START_LEVEL, level) - HERO_START_LEVEL);
}

/* ─────────────── Stars ─────────────── */

/** Прибавка урона от звёзд, промилле (одним модификатором: звёзды складываются, 5★ = +125 %). */
export function heroStarDamagePermille(b: BalanceData, stars: number): number {
  return PERMILLE + b.economy.stars.damagePerStarPermille * clampStars(stars);
}

/** Следующая звезда героя: номер (1…5), цена и требуемый уровень; `null` — уже 5★. */
export function nextStar(b: BalanceData, stars: number): { star: number; cost: number; requiredLevel: number } | null {
  const s = clampStars(stars);
  if (s >= MAX_STARS) return null;
  return { star: s + 1, cost: b.economy.stars.costs[s]!, requiredLevel: b.economy.stars.levels[s]! };
}

/**
 * Эффекты звёзд героя как модификаторы для боя: урон героя `mulPermille +250·stars` (один модификатор)
 * и `starUpgrades` навыка за звёзды 1…stars (каждая звезда — свой модификатор, они перемножаются: навык ≈ ×2 на 5★).
 */
export function heroStarModifiers(b: BalanceData, heroId: HeroId, stars: number): StatModifier[] {
  const s = clampStars(stars);
  if (s === 0) return [];
  const hero = heroDef(b, heroId);
  const skill = b.skills.find((x) => x.id === hero.skillId);
  return [
    { stat: "damage", op: "mulPermille", value: b.economy.stars.damagePerStarPermille * s, target: heroId },
    ...(skill ? skill.starUpgrades.slice(0, s).flat() : []),
  ];
}

/* ─────────────── урон и сила ─────────────── */

/** Урон одного попадания героя с учётом уровня и звёзд (без усилений забежки), вниз. */
export function heroDamage(b: BalanceData, heroId: HeroId, level: number, stars: number): number {
  const h = heroDef(b, heroId);
  return Math.floor(
    (h.attack.damage * heroLevelDamagePermille(h, level) * heroStarDamagePermille(b, stars)) / (PERMILLE * PERMILLE),
  );
}

/**
 * «Сила» героя для HUD и вкладок: урон в секунду по одной цели с уровнем и звёздами, вниз
 * (без навыка, площади и усилений забежки; считается из точного урона, а не из округлённого {@link heroDamage}).
 */
export function heroPower(b: BalanceData, heroId: HeroId, level: number, stars: number): number {
  const h = heroDef(b, heroId);
  const num = h.attack.damage * heroLevelDamagePermille(h, level) * heroStarDamagePermille(b, stars) * TICK_RATE;
  return Math.floor(num / (PERMILLE * PERMILLE * h.attack.cooldownTicks));
}

/** Суммарная сила открытых героев. */
export function totalPower(b: BalanceData, heroes: readonly HeroProgress[]): number {
  return heroes.reduce((sum, p) => (p.unlocked ? sum + heroPower(b, p.heroId, p.level, p.stars) : sum), 0);
}

/* ─────────────── Tech ─────────────── */

/** Уровень Tech из записи игрока (нет записи — 0), обрезанный до максимума. */
export function techLevel(t: TechDef, levels: Readonly<Record<string, number>>): number {
  return Math.min(t.maxLevel, Math.max(0, levels[t.id] ?? 0));
}

/** Цена уровня Tech `level → level + 1`; `null` — максимум или неизвестный id. */
export function techLevelCost(b: BalanceData, techId: string, level: number): number | null {
  const t = techDef(b, techId);
  if (!t || level >= t.maxLevel) return null;
  return curveCost(t.cost, level);
}

/** Эффекты всех купленных Tech как модификаторы (уровни одной Tech складываются, см. `foldLevels`). */
export function techModifiers(b: BalanceData, levels: Readonly<Record<string, number>>): StatModifier[] {
  return b.tech.flatMap((t) => foldLevels(t.modifiers, techLevel(t, levels)));
}

/** Потолок офлайн-дохода, секунд: база (8 ч) + склад. */
export function offlineCapSeconds(b: BalanceData, levels: Readonly<Record<string, number>>): number {
  return b.tech.reduce(
    (sum, t) => sum + t.offlineCapSecondsPerLevel * techLevel(t, levels),
    b.economy.offline.capSeconds,
  );
}

/* ─────────────── новый игрок и открытие ─────────────── */

/** Прогресс героев нового игрока: все на уровне 1 без звёзд, открыты герои с `unlockWave ≤ 1`. */
export function initialHeroes(b: BalanceData): HeroProgress[] {
  return b.heroes.map((h) => ({ heroId: h.id, unlocked: h.unlockWave <= 1, level: HERO_START_LEVEL, stars: 0 }));
}

/** Открывает героев, чья волна открытия ≤ `wave` (новый массив; уже открытые не трогает). */
export function unlockHeroes(b: BalanceData, heroes: readonly HeroProgress[], wave: number): HeroProgress[] {
  return heroes.map((p) => (p.unlocked || heroDef(b, p.heroId).unlockWave > wave ? p : { ...p, unlocked: true }));
}

function clampStars(stars: number): number {
  return Math.min(MAX_STARS, Math.max(0, stars));
}
